/*
 * Go Kids — send signup forms to Klaviyo instead of creating Shopify customers.
 *
 * Progressive enhancement over the native {% form 'customer' %} markup: the forms
 * render and submit to Shopify exactly as before if this script never runs, or if
 * the theme setting is off. Nothing here touches the contact form — routing support
 * enquiries into Klaviyo would record marketing consent nobody gave.
 *
 * The listener is DELEGATED on document, not bound per form. assets/variant-picker.js
 * morphs the whole of <main> when a combined-listing flavour is switched, which
 * destroys any listener bound to the notify form; the form would then silently go
 * back to creating Shopify customers with no error anywhere.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('gk-klaviyo-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config.enabled || !config.companyId || !config.listId) return;
  // Theme-editor previews would otherwise write real profiles into the live list.
  if (config.designMode) return;

  // Pinned deliberately. Klaviyo rejects a missing revision and 410s a revision that
  // has aged out (~2 years), so this needs a diary note rather than "latest".
  var REVISION = '2026-07-15';
  var BASE = 'https://a.klaviyo.com/client';

  function endpoint(path) {
    return BASE + path + '?company_id=' + encodeURIComponent(config.companyId);
  }

  function post(path, body) {
    return fetch(endpoint(path), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      // Klaviyo answers with Access-Control-Allow-Origin: *, which is a hard CORS
      // failure if credentials are included.
      credentials: 'omit',
      body: JSON.stringify(body),
    });
  }

  function subscribe(email, form) {
    var properties = {};
    var source = form.getAttribute('data-gk-source') || 'Website';
    var listId = (form.getAttribute('data-gk-list-id') || '').trim() || config.listId;
    properties['Signup Source'] = source;
    // Match the embedded form's profile source only where explicitly requested.
    // custom_source records subscription method detail; it is not this property.
    var profileSource = form.getAttribute('data-gk-profile-source');
    if (profileSource) properties['$source'] = profileSource;

    var product = form.getAttribute('data-gk-product');
    var profile = {
      type: 'profile',
      attributes: {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
        properties: properties,
      },
    };

    // Append rather than overwrite: a parent who asks about three flavours should
    // end up with three, not just the last one.
    if (product) {
      profile.meta = { patch_properties: { append: { 'Requested Products': product } } };
    }

    return post('/subscriptions/', {
      data: {
        type: 'subscription',
        attributes: { custom_source: source, profile: { data: profile } },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    });
  }

  // A discrete event per request, so a launch flow can trigger off it and the client
  // can segment by flavour. Profile properties alone can't trigger a flow.
  function trackRequest(email, form) {
    var product = form.getAttribute('data-gk-product');
    if (!product) return Promise.resolve();

    return post('/events/', {
      data: {
        type: 'event',
        attributes: {
          properties: {
            Product: product,
            ProductID: form.getAttribute('data-gk-product-id') || '',
            ProductHandle: form.getAttribute('data-gk-product-handle') || '',
            Source: form.getAttribute('data-gk-source') || '',
            URL: window.location.href,
          },
          metric: { data: { type: 'metric', attributes: { name: 'Requested Launch Notification' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
      },
    })
      .then(function (response) {
        // Swallowed deliberately — the subscription already succeeded — but leave a
        // breadcrumb, because a silently broken metric would stop the launch flow
        // triggering with no other symptom.
        if (response.status !== 202) console.warn('[gk-klaviyo] event rejected', response.status);
      })
      .catch(function () {});
  }

  var GENERIC_ERROR = 'We couldn’t save your email just now. Please try again.';

  function setMessage(el, text) {
    if (!el) return;
    // The footer error node carries an inline SVG icon; textContent on the container
    // would delete it, so prefer a dedicated text span when one exists.
    var target = el.querySelector('[data-gk-klaviyo-error-text]');
    if (target) target.textContent = text;
    else el.textContent = text;
  }

  function show(el) {
    if (el) el.hidden = false;
  }

  function hide(el) {
    if (el) el.hidden = true;
  }

  function setBusy(form, busy) {
    var button = form.querySelector('button[type="submit"], button:not([type])');
    if (button) button.disabled = busy;
  }

  function handle(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-gk-klaviyo]')) return;

    // Claim the submit before inspecting anything: every exit from here must keep the
    // event away from Shopify. stopImmediatePropagation is what actually stops the
    // storefront captcha script, which re-submits natively via form.submit().
    event.preventDefault();
    event.stopImmediatePropagation();

    var emailInput = form.querySelector('input[type="email"]');
    if (!emailInput || !emailInput.value) return;
    if (form.checkValidity && !form.checkValidity()) return;

    var successEl = form.querySelector('[data-gk-klaviyo-success]');
    var errorEl = form.querySelector('[data-gk-klaviyo-error]');
    var fields = form.querySelector('[data-gk-klaviyo-fields]');

    // Bots fill everything they can see. A human never touches this.
    var honeypot = form.querySelector('[data-gk-klaviyo-hp]');
    if (honeypot && honeypot.value) {
      hide(errorEl);
      hide(fields);
      show(successEl);
      return;
    }

    setBusy(form, true);
    hide(errorEl);

    var email = emailInput.value.trim();

    subscribe(email, form)
      .then(function (response) {
        // 202 means queued, not confirmed — Klaviyo resolves duplicates and
        // suppression asynchronously, so this is as certain as the browser can be.
        if (response.status !== 202) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
              var err = new Error(detail || GENERIC_ERROR);
              // Only messages we have seen and judged shopper-appropriate get rendered.
              err.gkUserFacing = true;
              throw err;
            });
        }

        trackRequest(email, form);

        // Stitch this signup to the browsing session Klaviyo already tracks.
        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
            window.klaviyo.identify({ email: email });
          }
        } catch (e) {
          /* never block the success state on analytics */
        }

        hide(fields);
        show(successEl);
        if (successEl && typeof successEl.focus === 'function') successEl.focus();
      })
      .catch(function (error) {
        setBusy(form, false);
        if (errorEl) {
          // A rejected fetch is a TypeError whose message is browser noise
          // ("Failed to fetch"). a.klaviyo.com is on common privacy blocklists, so
          // this path is reached by real shoppers, not just in theory.
          if (!error.gkUserFacing) console.warn('[gk-klaviyo]', error);
          // show first: mutating a live region while it is display:none is not announced
          show(errorEl);
          setMessage(errorEl, error.gkUserFacing ? error.message : GENERIC_ERROR);
        }
      });
  }

  // Capture phase so we run before anything that might stop propagation.
  document.addEventListener('submit', handle, true);
})();
